/**
 * Iter 67cj — Travel Tips strip, CMS-powered (Phase 3).
 *
 * Fetches `/public/blog?category=travel-tips&limit=3` so admins can publish
 * new tips from /admin → Platform operations → Blog → Travel tips.
 *
 * If the CMS returns nothing yet we fall through to a small curated list of
 * topics with "coming soon" copy so the homepage section never blanks out.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { fileUrl } from "@/lib/api";
import { Compass, Fuel, ParkingCircle, Map as MapIcon, ArrowRight, BookOpen } from "lucide-react";

const FALLBACK_TIPS = [
  { id: "fb-1", title: "Driving rules across the Balkans",         country: "Multi-country", excerpt: "Local speed limits, alcohol rules, and what to carry in the glovebox.",     icon: Compass },
  { id: "fb-2", title: "Where to fill up in rural Italy",          country: "Italy",         excerpt: "Petrol-station maps, EV chargers, and the unattended-pump trick.",         icon: Fuel },
  { id: "fb-3", title: "Smart parking tips for the Croatian coast", country: "Croatia",      excerpt: "Where to park free, what blue zones mean, and ferry-queue etiquette.",      icon: ParkingCircle },
];

function _resolveSrc(path) {
  if (!path) return null;
  return /^https?:\/\//i.test(path) ? path : fileUrl(path);
}

export default function TravelTipsSection() {
  const [posts, setPosts] = useState(null); // null = loading, [] = empty
  useEffect(() => {
    let alive = true;
    api.get("/public/blog", { params: { category: "travel-tips", limit: 3 } })
      .then((r) => { if (alive) setPosts(r.data || []); })
      .catch(() => { if (alive) setPosts([]); });
    return () => { alive = false; };
  }, []);

  const isLive = !!(posts && posts.length > 0);
  const items = isLive ? posts : FALLBACK_TIPS;

  return (
    <section className="bg-white py-20" data-testid="travel-tips-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between mb-10 flex-wrap gap-3">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-neutral-500 font-semibold mb-3 flex items-center gap-1.5">
              <MapIcon className="h-3 w-3" /> Travel tips for renters
            </div>
            <h2 className="font-display text-3xl lg:text-4xl tracking-tight font-medium">Drive smarter, save more</h2>
            <p className="mt-2 text-neutral-600 text-sm max-w-xl">Local know-how on driving rules, parking, fuel, and border crossings — written by RentalCar.al hosts.</p>
          </div>
          <Link to="/blog/travel-tips" className="text-sm font-medium text-blue-600 hover:underline" data-testid="travel-tips-see-all">
            Read all articles <ArrowRight className="inline h-3.5 w-3.5" />
          </Link>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map((t) => {
            const Icon = t.icon || BookOpen;
            const inner = (
              <>
                {isLive && t.featured_image ? (
                  <div className="aspect-[4/3] overflow-hidden bg-neutral-100 rounded-2xl mb-5">
                    <img
                      src={_resolveSrc(t.featured_image)}
                      alt={t.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                  </div>
                ) : (
                  <div className="h-12 w-12 rounded-xl bg-blue-100 flex items-center justify-center mb-4 group-hover:bg-blue-200 transition">
                    <Icon className="h-5 w-5 text-blue-700" />
                  </div>
                )}
                <div className="text-[10px] uppercase tracking-[0.18em] text-neutral-500 font-bold mb-2">
                  {t.country || "Europe"}
                </div>
                <h3 className="font-display text-lg font-semibold text-neutral-900 group-hover:text-blue-700 transition">{t.title}</h3>
                <p className="text-xs text-neutral-500 mt-2 line-clamp-2">
                  {t.excerpt || (isLive ? "Read the full article" : "Full article coming soon — get notified when it goes live.")}
                </p>
              </>
            );
            return t.slug ? (
              <Link
                key={t.id || t.slug}
                to={`/blog/travel-tips/${t.slug}`}
                className="group rounded-3xl bg-gradient-to-br from-neutral-50 to-white border border-border p-6 hover:shadow-lg transition-all"
                data-testid={`travel-tip-${t.slug}`}
              >
                {inner}
              </Link>
            ) : (
              <article
                key={t.id}
                className="group rounded-3xl bg-gradient-to-br from-neutral-50 to-white border border-border p-6 hover:shadow-lg transition-all"
                data-testid={`travel-tip-${t.id}`}
              >
                {inner}
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
